import { useMemo, useState } from 'react';
import { Alert, Button, Collapse, Modal, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ExclamationTriangle } from 'react-bootstrap-icons';
import { computeHygieneIssues, type HygieneIssue } from '@/config/clientConfigFields';
import { clientService } from '@/services/clientService';
import { type ClientConfig } from '@/types';
import './ConfigHygienePanel.css';

interface ConfigHygienePanelProps {
  clientName: string;
  config: ClientConfig;
  disabled?: boolean;
  /** Shown when the panel's actions are disabled (e.g. unsaved form edits). */
  disabledNote?: string;
  /** Called with the removed keys so the parent can patch its local state. */
  onCleaned: (removedKeys: string[]) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const formatValue = (value: unknown): string => {
  try {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 77)}...` : s;
  } catch {
    return String(value);
  }
};

const settings = (n: number) => (n === 1 ? '1 setting' : `${n} settings`);

/**
 * FEAT-239: an expandable panel listing the deprecated and unknown keys a
 * client's config still holds, with one-click removal. Removal uses the
 * targeted REMOVE path (updateClientConfig with undefined values), so it works
 * even for keys the current schema no longer recognises.
 */
export function ConfigHygienePanel({
  clientName,
  config,
  disabled = false,
  disabledNote,
  onCleaned,
  onError,
  onSuccess,
}: ConfigHygienePanelProps) {
  const [open, setOpen] = useState(false);
  const [pendingKeys, setPendingKeys] = useState<string[] | null>(null);
  const [working, setWorking] = useState(false);

  const issues = useMemo(() => computeHygieneIssues(config as Record<string, unknown>), [config]);

  if (issues.length === 0) return null;

  const deprecated = issues.filter((i) => i.kind === 'deprecated');
  const unknown = issues.filter((i) => i.kind === 'unknown');
  // "Remove all deprecated" skips keys with an active caution (e.g. legacy
  // Synergy flags a record still relies on) — those need a deliberate click.
  const safeDeprecatedKeys = deprecated.filter((i) => !i.warning).map((i) => i.key);

  const removeKeys = async (keys: string[]) => {
    try {
      setWorking(true);
      const updates = Object.fromEntries(keys.map((k) => [k, undefined])) as Partial<ClientConfig>;
      await clientService.updateClientConfig(clientName, updates);
      onSuccess(
        `Removed ${settings(keys.length)} from ${clientName}: ${keys.join(', ')}. The client picks this up at its next deploy.`
      );
      onCleaned(keys);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      onError(`Nothing was removed from ${clientName}.${detail}`);
    } finally {
      setWorking(false);
      setPendingKeys(null);
    }
  };

  const renderRows = (list: HygieneIssue[]) =>
    list.map((issue) => (
      <tr key={issue.key}>
        <td className="mono">{issue.key}</td>
        <td className="mono">{formatValue(issue.value)}</td>
        <td>
          {issue.reason}
          {issue.warning && (
            <div className="config-hygiene__caution">
              <ExclamationTriangle aria-hidden="true" />
              <span>{issue.warning}</span>
            </div>
          )}
        </td>
        <td className="config-hygiene__action">
          <Button
            variant="outline-danger"
            size="sm"
            disabled={disabled || working}
            onClick={() => setPendingKeys([issue.key])}
          >
            Remove
          </Button>
        </td>
      </tr>
    ));

  const renderTable = (list: HygieneIssue[], reasonHeading: string) => (
    <div className="config-hygiene__table">
      <Table className="mb-0">
        <thead>
          <tr>
            <th className="config-hygiene__col-key">Setting</th>
            <th className="config-hygiene__col-value">Stored value</th>
            <th>{reasonHeading}</th>
            <th className="config-hygiene__col-action" />
          </tr>
        </thead>
        <tbody>{renderRows(list)}</tbody>
      </Table>
    </div>
  );

  const pendingCount = pendingKeys?.length ?? 0;

  return (
    <>
      <div className="config-hygiene">
        <button type="button" className="config-hygiene__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          <ExclamationTriangle className="config-hygiene__icon" aria-hidden="true" />
          <span className="config-hygiene__title">{settings(issues.length)} to clean up</span>
          <span className="config-hygiene__sub">
            This config still holds settings Numa no longer reads, or that this portal does not know.
          </span>
        </button>
        <Collapse in={open}>
          <div>
            <div className="config-hygiene__body">
              {disabled && disabledNote && <p className="config-hygiene__note">{disabledNote}</p>}

              {deprecated.length > 0 && (
                <section className="config-hygiene__group">
                  <div className="config-hygiene__group-head">
                    <h3 className="section-label">Deprecated settings</h3>
                    {safeDeprecatedKeys.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={disabled || working}
                        onClick={() => setPendingKeys(safeDeprecatedKeys)}
                      >
                        Remove all {safeDeprecatedKeys.length} deprecated
                      </Button>
                    )}
                  </div>
                  <p className="config-hygiene__note">
                    Before you remove one, check that the image this client runs, or the one you deploy next, no longer
                    reads it. An older image that still reads a removed setting uses its default at the next deploy.
                  </p>
                  {renderTable(deprecated, 'Why it can go')}
                </section>
              )}

              {unknown.length > 0 && (
                <section className="config-hygiene__group">
                  <div className="config-hygiene__group-head">
                    <h3 className="section-label">Unknown settings</h3>
                  </div>
                  <p className="config-hygiene__note">
                    These can come from a newer version of the config than this portal knows, so check before you remove
                    one. Removing one is also the fix when an older image refuses it at deploy with{' '}
                    <span className="mono">unrecognized_keys</span>.
                  </p>
                  {renderTable(unknown, 'Note')}
                </section>
              )}

              <p className="config-hygiene__note mb-0">
                Removing a setting changes the stored config only. Nothing is deployed, and the client picks the change
                up at its next deploy.
              </p>
            </div>
          </div>
        </Collapse>
      </div>

      <Modal show={pendingKeys !== null} onHide={() => !working && setPendingKeys(null)} centered>
        <Modal.Header closeButton={!working}>
          <Modal.Title>Remove {settings(pendingCount)}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This takes {pendingCount === 1 ? 'this setting' : 'these settings'} out of the stored config for{' '}
            <strong>{clientName}</strong>:
          </p>
          <ul className="config-hygiene__pending">
            {(pendingKeys ?? []).map((k) => {
              const warning = issues.find((i) => i.key === k)?.warning;
              return (
                <li key={k}>
                  <span className="mono">{k}</span>
                  {warning && (
                    <div className="config-hygiene__caution">
                      <ExclamationTriangle aria-hidden="true" />
                      <span>{warning}</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <Alert variant="warning" className="mb-0">
            Nothing is deployed now. The client picks the change up at its next deploy, so check that the image you
            deploy next no longer reads {pendingCount === 1 ? 'this setting' : 'these settings'}. The removal is
            recorded in the activity log.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPendingKeys(null)} disabled={working}>
            Cancel
          </Button>
          <Button variant="outline-danger" disabled={working} onClick={() => pendingKeys && removeKeys(pendingKeys)}>
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing
              </>
            ) : (
              `Remove ${settings(pendingCount)}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ConfigHygienePanel;
